import {
  CalculatorOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  HistoryOutlined,
  HomeOutlined,
  MailOutlined,
  PercentageOutlined,
  SettingOutlined,
  ShoppingOutlined,
  TeamOutlined,
  TransactionOutlined,
} from '@ant-design/icons';
import { canAccessPath } from '../../../app/router/accessControl';
import { AI_STUDIO_ROUTES } from '../../../features/aiStudio/model/studioConfig';

export function getNavShowcaseId(pathname = '') {
  const normalized = String(pathname || '')
    .trim()
    .replace(/^\//, '')
    .replace(/\/+$/, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '');

  return `nav-${normalized || 'dashboard'}`;
}

export const navEntries = [
  {
    key: '/dashboard',
    title: 'Dashboard',
    hint: 'Daily business pulse',
    section: 'Workspace',
    icon: HomeOutlined,
  },
  {
    key: '/activity-history',
    title: 'Activity History',
    hint: 'Track who changed what',
    section: 'Workspace',
    icon: HistoryOutlined,
  },
  {
    key: '/campaigns',
    title: 'Campaigns',
    hint: 'Send workspace-wide promotions',
    section: 'Workspace',
    icon: MailOutlined,
  },
  {
    key: '/ai-studio/campaigns',
    title: 'AI Studio',
    hint: 'Run guided AI workflows',
    section: 'Workspace',
    icon: MailOutlined,
    feature: 'aiStudio',
  },
  {
    key: '/invoices',
    title: 'Invoices',
    hint: 'Bill and track payments',
    section: 'Workspace',
    icon: FileDoneOutlined,
  },
  {
    key: '/estimated-invoices',
    title: 'Estimated Invoices',
    hint: 'Share draft billing values',
    section: 'Workspace',
    icon: CalculatorOutlined,
  },
  {
    key: '/quotations',
    title: 'Quotations',
    hint: 'Prepare customer proposals',
    section: 'Workspace',
    icon: FileSearchOutlined,
  },
  {
    key: '/ledger',
    title: 'Sales Book',
    hint: 'Review sales history',
    section: 'Workspace',
    icon: TransactionOutlined,
  },
  {
    key: '/products',
    title: 'Products',
    hint: 'Maintain item catalog',
    section: 'Records',
    icon: ShoppingOutlined,
  },
  {
    key: '/customers',
    title: 'Customers',
    hint: 'Keep contact details clean',
    section: 'Records',
    icon: TeamOutlined,
  },
  {
    key: '/taxes',
    title: 'Taxes',
    hint: 'Manage tax rules centrally',
    section: 'Records',
    icon: PercentageOutlined,
  },
  {
    key: '/document-templates',
    title: 'Document Templates',
    hint: 'Design reusable PDF layouts',
    section: 'System',
    icon: FileSearchOutlined,
  },
  {
    key: '/document-form-layouts',
    title: 'Form Layouts',
    hint: 'Arrange invoice, estimate, and quotation forms',
    section: 'System',
    icon: FileDoneOutlined,
  },
  {
    key: '/settings',
    title: 'Settings',
    hint: 'Company profile and billing identity',
    section: 'System',
    icon: SettingOutlined,
  },
];

export const navSectionOrder = ['Workspace', 'Records', 'System'];
const DASHBOARD_NAV_KEY = '/dashboard';

export const primaryNavKeys = [
  DASHBOARD_NAV_KEY,
  '/activity-history',
  '/campaigns',
  '/invoices',
  '/estimated-invoices',
  '/quotations',
  '/ledger',
];

const AI_STUDIO_NAV_PATHS = [
  AI_STUDIO_ROUTES.campaigns,
  AI_STUDIO_ROUTES.salesDocuments,
  AI_STUDIO_ROUTES.documentStudio,
];

function resolveAiStudioNavPath(role, options) {
  return AI_STUDIO_NAV_PATHS.find((path) => canAccessPath(path, role, options)) || '';
}

export function canAccessNavEntry(entry, role, { enableAuth = true, permissions } = {}) {
  if (!enableAuth || !entry) {
    return Boolean(entry);
  }

  if (entry.feature === 'aiStudio') {
    return Boolean(resolveAiStudioNavPath(role, { enableAuth, permissions }));
  }

  return canAccessPath(entry.key, role, { enableAuth, permissions });
}

export function getVisibleNavEntries(role, options) {
  return navEntries
    .map((entry) => {
      if (entry.feature !== 'aiStudio') {
        return entry;
      }

      const resolvedPath = resolveAiStudioNavPath(role, options);
      return resolvedPath ? { ...entry, key: resolvedPath } : null;
    })
    .filter((entry) => canAccessNavEntry(entry, role, options));
}

export function getSelectedNavKey(pathname, entries = navEntries) {
  if (pathname === '/') {
    return DASHBOARD_NAV_KEY;
  }

  if (pathname.startsWith('/ai-studio/')) {
    return entries.find((entry) => entry.feature === 'aiStudio')?.key || AI_STUDIO_ROUTES.campaigns;
  }

  return (
    entries.find(
      (entry) =>
        pathname === entry.key || pathname.startsWith(`${entry.key}/`)
    )?.key || DASHBOARD_NAV_KEY
  );
}

export function getNavEntry(pathname, entries = navEntries) {
  return entries.find((entry) => entry.key === getSelectedNavKey(pathname, entries)) || entries[0] || navEntries[0];
}

export function getPrimaryNavEntries(entries = navEntries) {
  return entries.filter((entry) => primaryNavKeys.includes(entry.key) || entry.feature === 'aiStudio');
}
